import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSpeech } from '@/hooks/use-speech';
import { useViewMode } from '@/lib/view-mode-context';
import { UiStrategy } from "@/ui/UiStrategy";
import type { SessionDetailPlayerResult } from "@/ui/view-props";
import { SessionDetailClassicView } from "@/ui/v1/SessionDetailClassicView";
import { SessionDetailModernView } from "@/ui/v2/SessionDetailModernView";
import { SessionDetailNeonView } from "@/ui/v3/SessionDetailNeonView";
import { UNIT_VALUE } from '@/lib/score';
import { calculateExpensePaidMap, calculateExpenseShareMap, minimiseTransactions } from '@/lib/settlement';
import { GoogleSheetSyncService } from '@/services/GoogleSheetSyncService';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { exportSessionXlsx } from '@/lib/xlsx-export';
import { useSession, useUpdateSession, useDeleteSession } from '@/hooks/use-sessions';
import { useSessionResults, useUpdateSessionResults, useAllSessionPlayers } from '@/hooks/use-session-players';
import { useExpenses } from '@/hooks/use-expenses';
import { formatSessionSummary, shareToWhatsApp } from '@/lib/whatsapp-share';

export default function SessionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isReadOnly, targetUserId } = useViewMode();
  const { toast } = useToast();
  
  const { data: session, isLoading: sessionLoading } = useSession(id);
  const { data: sessionResults = [], isLoading: resultsLoading } = useSessionResults(id);
  const { data: allExpenses = [], isLoading: expensesLoading } = useExpenses(targetUserId);
  const { data: allSessionPlayers = [], isLoading: spLoading } = useAllSessionPlayers(targetUserId);

  const updateSessionMutation = useUpdateSession();
  const updateResultsMutation = useUpdateSessionResults();
  const deleteSessionMutation = useDeleteSession();

  const loading = sessionLoading || resultsLoading || expensesLoading || spLoading;

  const [roast, setRoast] = useState('');
  const [generating, setGenerating] = useState(false);
  const { isSpeaking: roastSpeaking, speak, stop: stopRoast, unlock: unlockSpeech } = useSpeech();
  const [scoreImageUrl, setScoreImageUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDate, setEditedDate] = useState('');
  const [editedResults, setEditedResults] = useState<SessionDetailPlayerResult[]>([]);
  const [editedVenue, setEditedVenue] = useState('');
  const [editedNotes, setEditedNotes] = useState('');

  const derivedData = useMemo(() => {
    if (loading || !session) return null;

    const sessionExpenses = allExpenses.filter(e => e.session_id === id);
    const expensePaidMap = calculateExpensePaidMap(sessionExpenses);
    const expenseShareMap = calculateExpenseShareMap(sessionExpenses);

    const playerIds = sessionResults.map(sp => sp.player_id);
    const playerStreaks: Record<string, { streak: number, type: 'win' | 'loss' | 'none' }> = {};

    playerIds.forEach(pid => {
      const playerHistory = allSessionPlayers
        .filter(h => h.player_id === pid)
        .sort((a, b) => new Date((b.sessions as any)?.date || 0).getTime() - new Date((a.sessions as any)?.date || 0).getTime());
      
      let streak = 0;
      let type: 'win' | 'loss' | 'none' = 'none';

      if (playerHistory.length > 0) {
        const firstNet = playerHistory[0].net_amount || 0;
        if (firstNet > 0) {
          type = 'win';
          for (const h of playerHistory) {
            if ((h.net_amount || 0) > 0) streak++;
            else break;
          }
        } else if (firstNet < 0) {
          type = 'loss';
          for (const h of playerHistory) {
            if ((h.net_amount || 0) < 0) streak++;
            else break;
          }
        }
      }
      playerStreaks[pid] = { streak, type };
    });

    const results = sessionResults.map((sp: any) => ({
      id: sp.player_id,
      name: sp.players?.name || 'Unknown',
      nickname: sp.players?.nickname || null,
      buyIns: sp.buy_ins,
      cashOut: sp.cash_out || 0,
      net: sp.net_amount || 0,
      expensePaid: expensePaidMap[sp.player_id] || 0,
      expenseShare: expenseShareMap[sp.player_id] || 0,
      totalNet: (sp.net_amount || 0) + (expensePaidMap[sp.player_id] || 0) - (expenseShareMap[sp.player_id] || 0),
      streak: playerStreaks[sp.player_id]?.streak,
      streakType: playerStreaks[sp.player_id]?.type,
      avatar_url: sp.players?.avatar_url || null,
    })).sort((a, b) => b.totalNet - a.totalNet);

    const totalCashOut = sessionResults.reduce((sum, sp) => sum + (sp.cash_out || 0), 0);
    const totalExpenses = sessionExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

    return { results, sessionExpenses, totalCashOut, totalExpenses };
  }, [loading, session, sessionResults, allExpenses, allSessionPlayers, id]);

  useEffect(() => {
    if (session) {
      setEditedDate(session.date);
      setEditedVenue(session.venue || '');
      setEditedNotes(session.notes || '');
    }
  }, [session]);

  useEffect(() => {
    if (derivedData?.results) {
      setEditedResults(derivedData.results);
    }
  }, [derivedData?.results]);

  useEffect(() => {
    const path = session?.score_sheet_path;
    if (path) {
      const fetchImageUrl = async () => {
        const { data, error } = await supabase.storage
          .from('scoresheets')
          .createSignedUrl(path, 3600);
        
        if (data) {
          setScoreImageUrl(data.signedUrl);
        } else if (error) {
          console.error("Error fetching signed URL:", error);
        }
      };
      fetchImageUrl();
    }
  }, [session?.score_sheet_path]);

  const handleUploadScoreSheet = async (file: File) => {
    if (isReadOnly || !id) return;
    
    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();
      const filePath = `${targetUserId}/${id}/score.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('scoresheets')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      await updateSessionMutation.mutateAsync({
        id,
        score_sheet_path: filePath
      });

      toast({ title: 'Score sheet uploaded successfully!' });
      
      const { data } = await supabase.storage
          .from('scoresheets')
          .createSignedUrl(filePath, 3600);
      if (data) setScoreImageUrl(data.signedUrl);
      
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Error uploading score sheet', description: err.message });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!id || isReadOnly || !session) return;
    
    try {
      // 1. Update session date, venue, and notes
      await updateSessionMutation.mutateAsync({
        id,
        date: editedDate,
        venue: editedVenue.trim() || null,
        notes: editedNotes.trim() || null
      });

      // 2. Update each player's results
      const resultsToSave = editedResults.map(res => ({
        ...res,
        net: (res.cashOut || 0) - (res.buyIns * (session.buy_in_unit ?? UNIT_VALUE))
      }));

      await updateResultsMutation.mutateAsync({
        sessionId: id,
        results: resultsToSave
      });

      // 3. Trigger Google Sheets sync
      await GoogleSheetSyncService.syncSession(id);

      toast({ title: 'Session updated successfully!' });
      setIsEditing(false);
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Error saving session', description: err.message });
    }
  };

  const handleDelete = async () => {
    if (!id || isReadOnly || !targetUserId) return;

    try {
      // 1. Trigger Google Sheets sync delete for this session
      await GoogleSheetSyncService.syncDeleteSession(id);
      
      // 2. Delete the session
      await deleteSessionMutation.mutateAsync({ id, userId: targetUserId });

      toast({ title: 'Session deleted successfully' });
      const navigatePath = isReadOnly ? `/public/${targetUserId}/sessions` : '/sessions';
      navigate(navigatePath);
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Error deleting session', description: err.message });
    }
  };

  const generateRoast = async () => {
    if (roastSpeaking) {
      stopRoast();
      return;
    }

    unlockSpeech();
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-roast', {
        body: { results: derivedData?.results, sessionDate: session?.date },
      });

      if (error) throw error;
      const roastText = data.roast;
      setRoast(roastText);

      if (roastText) {
        speak(roastText);
      }
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Error', description: err.message });
    }
    setGenerating(false);
  };

  const copyRoast = () => {
    navigator.clipboard.writeText(roast);
    toast({ title: 'Copied to clipboard!' });
  };

  const shareWhatsApp = () => {
    if (!session || !derivedData) return;
    
    const prefix = isReadOnly ? `/public/${targetUserId}` : '';
    const url = `${window.location.origin}${prefix}/session/${id}`;
    
    const summary = formatSessionSummary({
      date: session.date,
      results: derivedData.results,
      totalCashOut: derivedData.totalCashOut,
      totalExpenses: derivedData.totalExpenses,
      venue: session.venue
    }, url);
    
    const shareText = roast ? `${summary}\n\nAI Roast 🔥:\n${roast}` : summary;
    shareToWhatsApp(shareText);
  };

  const handleRematch = () => {
    const ids = (derivedData?.results || []).map((r) => r.id).join(',');
    navigate(`/session/new?rematch=${ids}`);
  };

  const props = {
    loading,
    session,
    results: derivedData?.results || [],
    expenses: derivedData?.sessionExpenses || [],
    roast,
    generating,
    onGenerateRoast: generateRoast,
    roastSpeaking,
    onStopRoast: stopRoast,
    onCopyRoast: copyRoast,
    onShareWhatsApp: shareWhatsApp,
    onExportXlsx: (chartImage?: string) => {
      const txns = minimiseTransactions((derivedData?.results || []).map(r => ({ name: r.name, net: r.totalNet, avatar_url: r.avatar_url })));
      exportSessionXlsx(
        session?.date ?? '',
        derivedData?.results || [],
        derivedData?.sessionExpenses || [],
        session?.buy_in_unit ?? UNIT_VALUE,
        txns,
        session?.notes ?? null,
        session?.venue ?? null,
        chartImage
      );
    },
    totalCashOut: derivedData?.totalCashOut,
    totalExpenses: derivedData?.totalExpenses,
    isReadOnly,
    targetUserId,
    buyInUnit: session?.buy_in_unit ?? UNIT_VALUE,
    onRematch: isReadOnly ? undefined : handleRematch,
    venue: session?.venue ?? null,
    notes: session?.notes ?? null,
    editedVenue,
    setEditedVenue,
    editedNotes,
    setEditedNotes,
    isEditing,
    setIsEditing,
    editedDate,
    setEditedDate,
    editedResults,
    setEditedResults,
    onSave: handleSave,
    onDelete: async () => setShowDeleteConfirm(true),
    scoreImageUrl,
    onUploadScoreSheet: handleUploadScoreSheet,
    isUploading,
  };

  return (
    <>
      <UiStrategy
        classic={<SessionDetailClassicView {...props} />}
        modern={<SessionDetailModernView {...props} />}
        neon={<SessionDetailNeonView {...props} />}
      />
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Session"
        description="Are you sure you want to delete this session? This will also delete all related expenses and player results."
        confirmLabel="Delete Session"
        onConfirm={handleDelete}
      />
    </>
  );
}
